import { differenceInCalendarDays } from 'date-fns';
import type { Dispatch as ReactDispatch } from 'react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import useRequest from '@swf/core/hooks/useRequest';
import { select } from '@swf/core/store';

import type { Action } from '../../../hooks/useStateReducer';
import { DispatchActionType } from '../../../hooks/useStateReducer';
import type { ExcludePlayerResponse } from '../definitions';
import { selfExcludedUser } from '../util';

interface SelfExcludedData {
  daysRequested: number;
  minRevocationWaitPeriodDays: number;
  revocationWaitPeriodDays: number;
  selfExcludedInfoFlags: number;
  timeUntil: number;
  timeFrom: number;
}

const useSelfExclusionData = (
  response?: ExcludePlayerResponse,
  dispatchAction?: ReactDispatch<Action<ExcludePlayerResponse>>
) => {
  const [selfExcluded, setSelfExcluded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selfExclusionData, setSelfExclusionData] = useState<SelfExcludedData>();
  const [daysLeft, setDaysLeft] = useState(0);
  const isAuthenticated = useSelector(select.session.isAuthenticated);

  const request = useRequest();

  useEffect(() => {
    if (isAuthenticated) {
      setLoading(true);
      request({ service: 'responsiblegaming.getselfexcludedinfo' }).subscribe(({ body }) => {
        setLoading(false);

        if (selfExcludedUser(body)) {
          dispatchAction?.({
            type: DispatchActionType.ErrorCode,
            payload: body?.messages?.msg?.errcode
          });
          return;
        }

        const { selfExcluded, selfExcludedData } = body;
        setSelfExcluded(selfExcluded);
        if (selfExcludedData) {
          setSelfExclusionData(selfExcludedData);
          setDaysLeft(
            differenceInCalendarDays(selfExcludedData.timeUntil, selfExcludedData.timeFrom)
          );
        }
      });
    }
  }, [dispatchAction, request, response, isAuthenticated]);

  return { loading, selfExcluded, selfExclusionData, daysLeft };
};

export default useSelfExclusionData;
